import i18n, { type InitOptions } from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import Pseudo from 'i18next-pseudo';

import { SUPPORTED_LANGUAGES, NAMESPACES, DEFAULT_NAMESPACE, FALLBACK_LANGUAGE } from './constants';
import { detectionOptions, createLanguageDetector } from './detection';

// Import from src/locales so Vite can bundle English as the default resource.
// Other languages are loaded on-demand via HttpBackend from /public/locales/.
// Keep each bundled namespace file aligned with public/locales/en/<same>.json so keys are not missing at runtime.
import common from '../locales/en/common.json';
import dashboard from '../locales/en/dashboard.json';
import onboarding from '../locales/en/onboarding.json';
import settings from '../locales/en/settings.json';
import profile from '../locales/en/profile.json';
import nav from '../locales/en/nav.json';
import engineers from '../locales/en/engineers.json';
import teams from '../locales/en/teams.json';
import workItems from '../locales/en/workItems.json';
import repositories from '../locales/en/repositories.json';
import pullRequests from '../locales/en/pullRequests.json';
import leaderboard from '../locales/en/leaderboard.json';
import layout from '../locales/en/layout.json';
import govern from '../locales/en/govern.json';
import inspect from '../locales/en/inspect.json';

export const defaultResources = {
  en: {
    common,
    dashboard,
    onboarding,
    settings,
    profile,
    nav,
    engineers,
    teams,
    workItems,
    repositories,
    pullRequests,
    leaderboard,
    layout,
    govern,
    inspect,
  },
} as const;

// Create language detector instance with custom detectors registered
const languageDetector = createLanguageDetector();

// Detection for test environment (Vitest specifically)
const isVitest = typeof process !== 'undefined' && process.env['VITEST'] === 'true';

const instance = i18n;

// Only use HttpBackend if not in unit test mode
if (!isVitest) {
  instance.use(HttpBackend);
}

const initOptions: InitOptions = {
  // In unit tests, use bundled resources to avoid async loading issues and force English
  ...(isVitest ? { lng: 'en', resources: defaultResources } : {}),
  // Production: ship bundled English so the shell does not depend on a fetch
  // to /locales/... (Safari/Firefox + strict CSP, offline, or ad blockers can block or delay).
  ...(!isVitest ? { resources: defaultResources } : {}),
  react: {
    useSuspense: false,
  },
  fallbackLng: {
    'en-XA': ['en'],
    default: [FALLBACK_LANGUAGE],
  } as Record<string, string[]>,
  supportedLngs: [...SUPPORTED_LANGUAGES, 'en-XA'],
  ns: [...NAMESPACES],
  defaultNS: DEFAULT_NAMESPACE,
  debug: false,
  interpolation: {
    escapeValue: false,
  },
  partialBundledLanguages: true,
  backend: {
    loadPath: (lngs: string[], namespaces: string[]) => {
      const lng = lngs[0] === 'en-XA' ? 'en' : lngs[0];
      return `/locales/${lng}/${namespaces[0]}.json`;
    },
  },
  detection: detectionOptions,
  postProcess: ['pseudo'],
  // In Vitest, we want it to be synchronous
  initImmediate: !isVitest,
};

function syncDocumentLang(lng: string | undefined): void {
  if (typeof document === 'undefined' || !lng) return;
  const base = lng.split('-')[0] ?? lng;
  document.documentElement.lang = base;
  document.documentElement.dir = 'ltr';
}

instance.on('languageChanged', (lng) => {
  syncDocumentLang(lng);
});

void instance
  .use(languageDetector)
  .use(
    new Pseudo({
      enabled: true,
      languageToPseudo: 'en-XA',
      letters: {
        a: 'â',
        e: 'é',
        i: 'î',
        o: 'ô',
        u: 'û',
      },
    })
  )
  .use(initReactI18next)
  .init(initOptions)
  .then(() => {
    syncDocumentLang(instance.resolvedLanguage ?? instance.language);
  });

export default i18n;
